'use client';

import { useEffect, useState } from 'react';

import { Loader } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { VeradigmPolicyModal } from '~/app/_components/veradigm-policy/veradigm-policy-modal';

import { VeradigmUnlockInstructions } from '../sso/veradigm/veradigm-unlock-instructions';
import { parseVehrErrorMessage, parseVehrMagicCause } from './vehr-errors';

type VehrProcessingProps = {
  errorMessage: string | null;
  errorData?: unknown;
  onDifferentUser?: () => void;
};

export const VehrProcessing = ({
  errorMessage,
  errorData,
  onDifferentUser,
}: VehrProcessingProps) => {
  const t = useTranslations('ssoErrorDisplay');
  const [showUnlockInstructions, setShowUnlockInstructions] = useState(false);
  const {
    isTenantNotFoundError,
    isDifferentUserError,
    isLockedError,
    titleKey,
  } = parseVehrErrorMessage(errorMessage);

  useEffect(() => {
    if (isDifferentUserError && onDifferentUser) {
      onDifferentUser();
    }
  }, [isDifferentUserError, onDifferentUser]);

  if (isTenantNotFoundError) {
    return <VeradigmPolicyModal />;
  }

  if (!errorMessage || isDifferentUserError) {
    return (
      <main className="grid h-screen w-screen place-items-center">
        <Loader className="animate-spin" />
      </main>
    );
  }

  const titleHeading = t(`${titleKey}Title`);
  const titleSubtitle = t(`${titleKey}Subtitle`);

  const vehrCause = parseVehrMagicCause(errorData);
  const causeKey = `${titleKey}Cause`;
  const translatedCause = t.has(causeKey) ? t(causeKey) : null;
  const causeMessage = vehrCause || translatedCause || errorMessage;

  return (
    <main className="grid h-screen w-screen place-items-center px-4">
      <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 text-center">
        <div className="flex flex-col items-center gap-2">
          <h1 className="text-onLight-high text-2xl font-semibold">
            {titleHeading}
          </h1>
          <p className="text-onLight-medium text-sm">{titleSubtitle}</p>
        </div>

        {isLockedError ? (
          <VeradigmUnlockInstructions
            isVisible={showUnlockInstructions}
            onToggle={() => {
              setShowUnlockInstructions((visible) => !visible);
            }}
          />
        ) : null}

        {causeMessage ? (
          <div className="border-card-border bg-card-background w-full rounded-md border p-3 text-left">
            <p className="text-status-alert text-xs font-semibold uppercase tracking-wide">
              {t('detailsLabel')}
            </p>
            <pre className="text-onLight-high mt-1 whitespace-pre-wrap break-words text-sm">
              {causeMessage}
            </pre>
          </div>
        ) : null}
      </div>
    </main>
  );
};
